// Relative imports (not `@/`) so bun can test this module.
import { getHexTileMapKey } from '../hexmap/model/keys';
import type { HexTileMap } from '../hexmap/model/types';
import type { RuleSet } from '../rules';
import {
  type Building,
  type Cell,
  type EntityId,
  engagements,
  gatherResource,
  type SimState,
  toMilli,
  type Unit,
} from '../sim';
import { HEAL_ICON, healings, healVerb, REPAIR_ICON } from './heals';
import { RESOURCE_ICONS } from './resources';

/**
 * Workers inside farm or forestry `b`, counted as its 👷 badge counts them:
 * every unit inside, which is what its slots hold (`roomFor`); for a rival's,
 * only those on its tiles `seen`. `full`: how many of them have a full load,
 * so don't gather (the sim's `workersInside` leaves them out) and wait there,
 * a manual worker until it's moved.
 */
export function farmWorkers(
  state: SimState,
  rules: RuleSet,
  b: Building,
  seen: (cell: Cell) => boolean = () => true
): { inside: number; full: number } {
  let inside = 0;
  let full = 0;
  for (const u of Object.values(state.units)) {
    if (u.inside !== b.id || !seen(u.pos)) continue;
    inside++;
    const capacity = rules.units[u.type]?.carryCapacity;
    if (u.carry && capacity !== undefined && u.carry.amount >= toMilli(capacity)) full++;
  }
  return { inside, full };
}

/**
 * Who works where, from one pass over the units, for `assignedWorkers`:
 * by building id, the units inside it (`full` as in `farmWorkers`) and the
 * automated or pinned workers of its owner on the road for it.
 */
export interface WorkerIndex {
  inside: ReadonlyMap<EntityId, { inside: number; full: number }>;
  hauling: ReadonlyMap<EntityId, number>;
}

export function workerIndex(state: SimState, rules: RuleSet): WorkerIndex {
  // Tile → the building covering it (its own tile or a footprint tile).
  const covering = new Map<string, Building>();
  for (const b of Object.values(state.buildings)) {
    covering.set(getHexTileMapKey(b.pos), b);
    for (const cell of b.footprint ?? []) covering.set(getHexTileMapKey(cell), b);
  }
  const inside = new Map<EntityId, { inside: number; full: number }>();
  const hauling = new Map<EntityId, number>();
  for (const u of Object.values(state.units)) {
    if (u.inside) {
      const entry = inside.get(u.inside) ?? { inside: 0, full: 0 };
      entry.inside++;
      const capacity = rules.units[u.type]?.carryCapacity;
      if (u.carry && capacity !== undefined && u.carry.amount >= toMilli(capacity)) entry.full++;
      inside.set(u.inside, entry);
    }
    // A pinned worker on the road for its job counts like an auto one.
    const pinned = u.order?.kind === 'gather' && u.order.pin ? u.order.target : undefined;
    if (!u.auto && !pinned) continue;
    const work = pinned ?? (u.auto?.phase === 'haul' ? u.auto.work : u.auto?.target);
    const b = work && covering.get(getHexTileMapKey(work));
    if (!b || b.owner !== u.owner || u.inside === b.id) continue;
    hauling.set(b.id, (hauling.get(b.id) ?? 0) + 1);
  }
  return { inside, hauling };
}

/**
 * Everyone working own farm, forestry or mine `b`: `inside` it (as
 * `farmWorkers` counts them, `full` likewise) plus the automated workers on
 * the road for it, `hauling` a load home from it or walking back to it (their
 * claimed work tile is one of its tiles). `total` is both, what its slots are
 * shared by. Only for the owner's view: out on the road they may be in fog.
 * `index`: this tick's `workerIndex` when the caller has it (`tickWorkers`).
 */
export function assignedWorkers(
  state: SimState,
  rules: RuleSet,
  b: Building,
  index: WorkerIndex = workerIndex(state, rules)
): { total: number; inside: number; hauling: number; full: number } {
  const { inside, full } = index.inside.get(b.id) ?? { inside: 0, full: 0 };
  const hauling = index.hauling.get(b.id) ?? 0;
  return { total: inside + hauling, inside, hauling, full };
}

/** A unit's veterancy rank as chevrons ("▲▲" for rank 2); empty at rank 0. */
export function chevrons(rank: number): string {
  return '▲'.repeat(rank);
}

/**
 * Per-tick lookup of the icon for what each unit is busy doing, for the
 * floating indicators. Mirrors the sim's own rules (combat targeting,
 * gathering, on-site construction) so the icon only shows while it's really
 * happening. Shelter is shown as text (UnitsPanel, the HUD's unit chip).
 * A healer mending someone shows ✚ (an engineer repairing, 🔧), only while
 * its patient stands where `seen` says the viewer can see, so the icon never
 * points at a wounded unit in the fog. `engaged`/`heals`: this tick's
 * `engagements`/`healings` when the caller has them (`tickCache.ts`).
 */
export function activityLookup(
  state: SimState,
  rules: RuleSet,
  terrain: HexTileMap,
  seen: (cell: Cell) => boolean = () => true,
  engaged: ReadonlyMap<EntityId, EntityId> = engagements(state, { terrain, rules }),
  heals: ReadonlyMap<EntityId, EntityId> = healings(state, rules)
): (unit: Unit) => string | null {
  const ctx = { terrain, rules };
  // Attacking or being attacked both count as in battle.
  const fighting = new Set<EntityId>();
  for (const [attacker, target] of engaged) {
    fighting.add(attacker);
    fighting.add(target);
  }

  return (unit) => {
    if (fighting.has(unit.id)) return '⚔️';
    const patient = heals.get(unit.id);
    if (patient) {
      const p = state.units[patient];
      if (!p || !seen(p.pos)) return null;
      return healVerb(rules, unit.type) === 'repair' ? REPAIR_ICON : HEAL_ICON;
    }
    const order = unit.order;
    if (order?.kind === 'build') {
      const site = state.buildings[order.buildingId];
      if (
        site &&
        site.buildProgress < 1 &&
        site.pos.q === unit.pos.q &&
        site.pos.r === unit.pos.r
      ) {
        return '🔨';
      }
      return null;
    }
    const resource = gatherResource(state, ctx, unit);
    if (!resource) return null;
    return RESOURCE_ICONS[resource] ?? '⛏️';
  };
}
